import * as React from 'react';
import { createEvent, type DynamicDataObservable, getClientApiUrl, getDynamicData, URLX } from '@avensia/excite';
import { useDynamicData } from '@avensia/excite/dynamic-data/use-dynamic-data';
import { postJson } from '@avensia/excite/client/api';

function getCode(request: Excite.Commercetools.WishlistRequest) {
  return request.packageKey ? `${request.packageKey}|${request.sku}` : request.sku;
}

export interface WishlistCommandEventData extends Record<string, any> {
  additionalProductAttributes?: object;
}

export enum WishlistUpdateCommand {
  Add,
  Remove,
  DeleteList,
}

export type WishlistUpdateEventType = {
  requestData: Excite.Commercetools.WishlistRequest;
  command: WishlistUpdateCommand;
  commandEventData?: WishlistCommandEventData;
  wishList?: Excite.Commercetools.WishlistApiModel;
};

export const [wishlistUpdateEvent] = createEvent<WishlistUpdateEventType>();

async function sendRequest(
  url: URLX,
  request: Excite.Commercetools.WishlistRequest,
  wishlistDynamicData: DynamicDataObservable<Excite.Commercetools.WishlistApiModel>,
  command: WishlistUpdateCommand,
  commandEventData?: WishlistCommandEventData,
) {
  try {
    const { listKey } = await wishlistDynamicData.current();
    request.listKey = listKey;
    const response = await postJson<Excite.Commercetools.WishlistResponse>(url, request);
    wishlistDynamicData.refresh().then(() => {
      if (response.success) {
        wishlistUpdateEvent.emit({
          requestData: request,
          command,
          commandEventData,
          wishList: wishlistDynamicData.currentSync(),
        });
      }
    });

    return response;
  } catch {
    wishlistDynamicData.refresh();
    return { success: false } as Excite.Commercetools.WishlistResponse;
  }
}

export async function addWishListItem(
  request: Excite.Commercetools.WishlistRequest,
  commandEventData?: WishlistCommandEventData,
) {
  const wishlistDynamicData = getDynamicData<Excite.Commercetools.WishlistApiModel>();
  const url = getClientApiUrl('wishlist/additem');
  return await sendRequest(url, request, wishlistDynamicData, WishlistUpdateCommand.Add, commandEventData);
}

export async function removeWishListItem(
  request: Excite.Commercetools.WishlistRequest,
  commandEventData?: WishlistCommandEventData,
) {
  const wishlistDynamicData = getDynamicData<Excite.Commercetools.WishlistApiModel>();

  const url = getClientApiUrl('wishlist/removeitem');
  return await sendRequest(url, request, wishlistDynamicData, WishlistUpdateCommand.Remove, commandEventData);
}

export async function deleteWishlist(request: Excite.Commercetools.WishlistRequest) {
  const wishlistDynamicData = getDynamicData<Excite.Commercetools.WishlistApiModel>();

  const url = getClientApiUrl('wishlist/deletelist');
  return await sendRequest(url, request, wishlistDynamicData, WishlistUpdateCommand.Remove);
}

export function useItemExistsInWishList(request: Excite.Commercetools.WishlistRequest) {
  const shouldUpdate = React.useCallback(
    (
      nextState: Excite.Commercetools.WishlistApiModel | undefined,
      prevState: Excite.Commercetools.WishlistApiModel | undefined,
    ) => {
      return (
        nextState?.items?.find(i => i.code === getCode(request)) !==
        prevState?.items?.find(i => i.code === getCode(request))
      );
    },
    [request],
  );
  const [wishList] = useDynamicData<Excite.Commercetools.WishlistApiModel>({ shouldUpdate });
  const item = wishList?.items?.find(i => i.code === getCode(request));
  return Boolean(item);
}

export function useWishListCount() {
  const [wishList] = useDynamicData<Excite.Commercetools.WishlistApiModel>();
  return wishList?.items?.length ?? 0;
}
